import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Provider } from '@supabase/supabase-js';
import { supabase, DbUser } from '../lib/supabase';
import { SecureStorage } from '../utils/secureStorage';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: User | null;
  profile: DbUser | null;
  signInWithAzure: () => Promise<{ provider: Provider; url: string; } | undefined>;
  signOut: () => Promise<void>;
  loading: boolean;
  profileLoading: boolean;
  isAdmin: boolean;
  forceRefreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  signInWithAzure: async () => undefined,
  signOut: async () => {},
  loading: true,
  profileLoading: true,
  isAdmin: false,
  forceRefreshProfile: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<DbUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Function to fetch user profile with timeout
  const fetchUserProfile = async (userId: string, timeoutMs: number = 10000) => {
    try {
      logger.log('🔍 fetchUserProfile: Starting profile fetch for userId:', userId);
      logger.log('🔍 fetchUserProfile: Current timestamp:', new Date().toISOString());
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), timeoutMs);
      });
      
      // Race the query against the timeout
      const queryPromise = supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();
      
      logger.log('🔍 fetchUserProfile: Waiting for query with', timeoutMs, 'ms timeout...');
      const { data: userProfile, error: profileError } = await Promise.race([
        queryPromise,
        timeoutPromise
      ]) as any;

      logger.log('📊 fetchUserProfile: Query completed', {
        hasData: !!userProfile,
        hasError: !!profileError,
        errorCode: profileError?.code,
        errorMessage: profileError?.message
      });

      if (profileError) {
        logger.error('❌ fetchUserProfile: Error fetching user profile:', profileError);
        logger.error('❌ fetchUserProfile: Full error details:', JSON.stringify(profileError, null, 2));
        
        // Don't sign out on permission errors during refresh - just create fallback profile
        if (profileError.code === '42501' || profileError.message?.includes('permission')) {
          logger.warn('⚠️ fetchUserProfile: Permission error - creating fallback profile instead of signing out');
        }
        
        // Return null to trigger fallback profile creation
        return null;
      }

      if (userProfile) {
        logger.log('User profile found:', userProfile);
        return userProfile;
      } else {
        logger.warn('No user profile found for userId:', userId);
        // Try to create a profile for this user if it doesn't exist
        logger.log('🔄 fetchUserProfile: Profile not found, attempting fallback creation...');
        try {
          const { data: authUser } = await supabase.auth.getUser();
          if (authUser.user && authUser.user.id === userId) {
            logger.log('🔄 fetchUserProfile: Creating missing profile for user:', authUser.user.email);
            
            // Try to fetch again immediately - triggers are usually fast
            logger.log('🔄 fetchUserProfile: Retrying profile fetch...');
            const { data: retryProfile } = await supabase
              .from('users')
              .select('*')
              .eq('auth_user_id', userId)
              .maybeSingle();
              
            if (retryProfile) {
              logger.log('✅ fetchUserProfile: Profile found on retry:', retryProfile.email);
              return retryProfile;
            }
            
            // If still not found, create it manually
            logger.log('🔄 fetchUserProfile: Still not found, creating manually...');
            const { data: newProfile, error: createError } = await supabase
              .from('users')
              .insert({
                auth_user_id: userId,
                email: authUser.user.email || '',
                full_name: authUser.user.user_metadata?.full_name || 
                          authUser.user.user_metadata?.name || 
                          authUser.user.email?.split('@')[0] || 'User',
                role: 'user'
              })
              .select()
              .single();
            
            if (createError) {
              logger.error('❌ fetchUserProfile: Failed to create profile:', createError);
              
              // If creation failed due to duplicate, try fetching again
              if (createError.code === '23505') { // Unique constraint violation
                logger.log('🔄 fetchUserProfile: Duplicate error, trying fetch again...');
                const { data: finalProfile } = await supabase
                  .from('users')
                  .select('*')
                  .eq('auth_user_id', userId)
                  .single();
                return finalProfile || null;
              }
              return null;
            } else {
              logger.log('✅ fetchUserProfile: Created new profile successfully:', newProfile.email);
              return newProfile;
            }
          }
        } catch (createErr) {
          logger.error('❌ fetchUserProfile: Error in fallback profile creation:', createErr);
        }
        return null;
      }
    } catch (error) {
      logger.error('❌ fetchUserProfile: Error in fetchUserProfile:', error);
      if (error instanceof Error && error.message === 'Profile fetch timeout') {
        logger.error('⏰ fetchUserProfile: Timeout reached - database query took too long');
      }
      return null;
    }
  };

  // Consolidated setup within a single useEffect for the listener
  useEffect(() => {
    // Start in loading state
    setLoading(true);
    setProfileLoading(true);

    // Add a safety timeout to prevent infinite loading - increased timeout
    const safetyTimeout = setTimeout(() => {
      logger.warn('⚠️ Auth loading timeout reached after 5 seconds');
      logger.warn('This might indicate corrupted session data or network issues');
      logger.warn('Force clearing loading states to prevent infinite loading screen');
      
      setLoading(false);
      setProfileLoading(false);
      
      // If no user is set by now, clear everything to show login
      if (!user) {
        logger.warn('⚠️ No user found after timeout - clearing all auth state');
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
        SecureStorage.removeSecureItem('userIsAdmin');
      }
    }, 5000); // 5 second timeout - more reasonable for network delays

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Clear safety timeout since we have an auth event
      clearTimeout(safetyTimeout);
      // Keep the realtime socket authorized with the current JWT. Without
      // this, RLS-checked postgres_changes and private channels stop
      // delivering after a token refresh in long-lived sessions. Covers all
      // events: SIGNED_OUT passes a null session, and the mid-INITIAL_SESSION
      // refresh below re-fires this callback as TOKEN_REFRESHED with the
      // fresh token.
      supabase.realtime.setAuth(session?.access_token ?? null);
      // Handle INITIAL_SESSION (this should fire on refresh with stored session)
      if (event === 'INITIAL_SESSION') {
        logger.log('🔄 AuthContext: INITIAL_SESSION event triggered');
        logger.log('📊 INITIAL_SESSION state:', {
          hasSession: !!session,
          userEmail: session?.user?.email,
          userId: session?.user?.id
        });
        
        if (session) {
          logger.log('🔄 AuthContext: Processing INITIAL_SESSION...');
          
          // Check if token is expired before refreshing
          const now = Math.floor(Date.now() / 1000);
          const tokenExp = session.expires_at || 0;
          const needsRefresh = tokenExp <= now + 300; // Refresh if expires within 5 minutes
          
          if (needsRefresh) {
            logger.log('🔄 AuthContext: Token expires soon, refreshing...');
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              logger.error('❌ AuthContext: Token refresh failed', refreshError);
              // Don't immediately sign out - try to continue with existing session
              logger.warn('⚠️ AuthContext: Continuing with existing session despite refresh failure');
            } else if (refreshData.session) {
              session = refreshData.session; // Use refreshed session
              logger.log('✅ AuthContext: Token refresh successful');
            }
          }
          
          setUser(session.user);
          setLoading(false); // Set loading to false immediately after user is set
          setProfileLoading(true);
          
          // Fetch profile in background with timeout and fallback
          const fetchProfileWithFallback = async () => {
            try {
              logger.log('🔍 AuthContext: Fetching profile for INITIAL_SESSION user...');
              
              // Try to fetch profile with 5 second timeout
              const userProfile = await fetchUserProfile(session!.user.id, 5000);
              
              if (userProfile) {
                logger.log('✅ AuthContext: INITIAL_SESSION profile found:', {
                  email: userProfile.email,
                  role: userProfile.role,
                  id: userProfile.id
                });
                setProfile(userProfile);
                setIsAdmin(userProfile.role === 'admin');
                SecureStorage.setSecureBooleanItem('userIsAdmin', userProfile.role === 'admin');
              } else {
                throw new Error('Profile not found or fetch failed');
              }
            } catch (error) {
               logger.warn('⚠️ AuthContext: Profile fetch failed, creating fallback profile:', error);
               
               // Create a working profile to prevent login loops
               const fallbackProfile: DbUser = {
                 id: session!.user.id, // Use auth user id as fallback
                 auth_user_id: session!.user.id,
                 email: session!.user.email || '',
                 full_name: session!.user.user_metadata?.full_name || 
                           session!.user.user_metadata?.name || 
                           session!.user.email?.split('@')[0] || 'User',
                 role: 'user',
                 department: null
               };
               
               logger.log('🔄 AuthContext: Setting fallback profile:', fallbackProfile);
               setProfile(fallbackProfile);
               setIsAdmin(false);
               SecureStorage.removeSecureItem('userIsAdmin');
            } finally {
               logger.log('🔄 AuthContext: INITIAL_SESSION processing complete');
               setProfileLoading(false);
            }
          };
          
          // Start profile fetch in background
          fetchProfileWithFallback();
        } else {
           // No session found initially - this is normal for first visit
           logger.log('ℹ️ AuthContext: No session found in INITIAL_SESSION (normal for first visit)');
           setUser(null);
           setProfile(null);
           setIsAdmin(false);
           SecureStorage.removeSecureItem('userIsAdmin');
           setLoading(false);
           setProfileLoading(false);
        }
      }
      // Handle SIGNED_IN (e.g., after explicit login/OAuth)
      else if (event === 'SIGNED_IN') {
         if (session?.user) {
            logger.log('🎉 AuthContext: SIGNED_IN event for user:', session.user.email);
            logger.log('👤 User details:', {
              id: session.user.id,
              email: session.user.email,
              provider: session.user.app_metadata?.provider,
              confirmedAt: session.user.email_confirmed_at
            });
            setUser(session.user);
            setProfileLoading(true);
            
            // Try to fetch profile immediately for OAuth sign-ins with timeout
            const fetchSignInProfile = async () => {
              try {
                logger.log('🔍 AuthContext: Fetching profile for new sign-in...');
                const userProfile = await fetchUserProfile(session!.user.id, 5000);
                if (userProfile) {
                  logger.log('✅ AuthContext: Profile found and set:', {
                    id: userProfile.id,
                    email: userProfile.email,
                    role: userProfile.role,
                    fullName: userProfile.full_name
                  });
                  setProfile(userProfile);
                  setIsAdmin(userProfile.role === 'admin');
                  SecureStorage.setSecureBooleanItem('userIsAdmin', userProfile.role === 'admin');
                } else {
                  throw new Error('Profile fetch returned null');
                }
              } catch (error) {
                logger.warn('⚠️ AuthContext: Profile fetch failed for SIGNED_IN, creating fallback:', error);
                
                // Create fallback profile for new sign-in
                const fallbackProfile: DbUser = {
                  id: session!.user.id,
                  auth_user_id: session!.user.id,
                  email: session!.user.email || '',
                  full_name: session!.user.user_metadata?.full_name || 
                            session!.user.user_metadata?.name || 
                            session!.user.email?.split('@')[0] || 'User',
                  role: 'user',
                  department: null
                };
                setProfile(fallbackProfile);
                setIsAdmin(false);
                SecureStorage.removeSecureItem('userIsAdmin');
              } finally {
                logger.log('🔄 AuthContext: SIGNED_IN processing complete, setting loading to false');
                setProfileLoading(false);
                setLoading(false);
              }
            };
            
            fetchSignInProfile();
         } else {
            logger.warn('AuthContext: SIGNED_IN event without session user?');
            setLoading(false);
            setProfileLoading(false);
         }
      }
      // Handle SIGNED_OUT
      else if (event === 'SIGNED_OUT') {
         logger.log('AuthContext: Signed out, clearing state.');
         setUser(null);
         setProfile(null);
         setIsAdmin(false);
         SecureStorage.removeSecureItem('userIsAdmin');
         setLoading(false);
         setProfileLoading(false);
      }
      // Handle TOKEN_REFRESHED (can usually be ignored if profile doesn't change on refresh)
      else if (event === 'TOKEN_REFRESHED') {
         if (session?.user && !user) { 
            setUser(session.user);
         }
         // Ensure loading is false if we somehow got here while loading
         setLoading(false);
         setProfileLoading(false);
      }
      // Handle other events like USER_UPDATED, PASSWORD_RECOVERY if needed
      // ... 
    });

    return () => {
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount


  const signInWithAzure = async () => {
    try {
      // Clear any existing session data before starting OAuth
      logger.log('🧹 signInWithAzure: Clearing existing session before OAuth...');
      await supabase.auth.signOut();
      
      // Clear localStorage items that might interfere
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('sb-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      logger.log('✅ signInWithAzure: Pre-OAuth cleanup completed');
      
      // Minimal wait to ensure cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // dynamically build the correct callback URL for this host
      const redirect = `${window.location.origin}/auth/callback`;
      logger.log('🚀 signInWithAzure: Starting OAuth with redirect:', redirect);
      logger.log('🔍 signInWithAzure: Current window.location.origin:', window.location.origin);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'openid email profile User.Read',
          redirectTo: redirect,
          skipBrowserRedirect: false,
        },
      });
      
      if (error) {
        logger.error('Azure SSO error:', error);
        
        // If OAuth fails, try to create a fallback session for testing
        if (error.message?.includes('server_error') || error.message?.includes('configuration')) {
          logger.warn('Azure OAuth configuration issue detected. This may be a temporary problem.');
          logger.warn('Please check Azure AD app registration and Supabase Azure provider settings.');
        }
        
        throw error;
      }
      
      return data;
    } catch (err) {
      logger.error('Azure SSO error:', err);
      throw err;
    }
  };


  const signOut = async () => {
    try {
      // Clear all auth-related storage securely
      SecureStorage.clearSensitiveData();

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Force clear the user and profile state
      setUser(null);
      setProfile(null);
      setIsAdmin(false);
    } catch (error) {
      logger.error('Sign out error:', error);
      // Even if there's an error, we want to clear the local state
      setUser(null);
      setProfile(null);
      setIsAdmin(false);
      throw error;
    }
  };

  const forceRefreshProfile = async () => {
    if (!user) return;
    
    setProfileLoading(true);
    try {
      const userProfile = await fetchUserProfile(user.id);
      if (userProfile) {
        setProfile(userProfile);
        setIsAdmin(userProfile.role === 'admin');
        SecureStorage.setSecureBooleanItem('userIsAdmin', userProfile.role === 'admin');
      }
    } catch (error) {
      logger.error('Error refreshing profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      signInWithAzure, 
      signOut, 
      loading,
      profileLoading,
      isAdmin,
      forceRefreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 